import React, { useEffect, useState } from 'react';
import { HiDownload, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import { IoCloseOutline, IoShareSocialOutline } from 'react-icons/io5';

/**
 * Sticky action bar for the selection (browse + View All) — the ONE entry
 * point for downloading or requesting files (project-based requests,
 * 2026-09-23: cards carry no per-file download/request action any more).
 * Pinned to the viewport whenever the selection is non-empty.
 *
 * Props:
 *   count      – number of selected files (bar renders nothing when 0)
 *   mode       – 'download' (staff / full access: the files are downloaded
 *                outright) or 'request' (everyone else: a download request
 *                under a project). Defaults to 'request' — a caller that
 *                forgets the prop must never promise a download.
 *   onDownload – run the action for the whole selection
 *   onClear    – clear the selection
 *   items      – optional [{ key, name }] of the selected files; when given,
 *                the count opens a review list with a × per file. The
 *                selection survives new searches, so files can be selected
 *                that are no longer on screen — this is where they can be
 *                seen and dropped one by one.
 *   onRemove   – (key) => void, drops one file from the selection
 *   onShare    – optional; shares the whole selection. Pages pass it for staff
 *                only (only staff can create shares); without it there is no
 *                Share button.
 */
export default function DownloadSelectionBar({ count = 0, mode = 'request', onDownload, onClear, items = null, onRemove, onShare }) {
    const [listOpen, setListOpen] = useState(false);

    // the bar hides at 0 without unmounting: don't reopen with the list up
    useEffect(() => { if (!count) setListOpen(false); }, [count]);

    if (!count) return null;

    return (
        <div
            className="download-selection-bar"
            role="toolbar"
            aria-label="Selected files"
            onKeyDown={(e) => { if (e.key === 'Escape' && listOpen) { setListOpen(false); } }}
        >
            {items ? (
                <button type="button" className="download-selection-count download-selection-count--btn"
                    aria-expanded={listOpen} aria-controls={listOpen ? 'download-selection-list' : undefined} onClick={() => setListOpen((v) => !v)}>
                    {count} {count === 1 ? 'file' : 'files'} selected
                    {listOpen ? <HiChevronDown size={14} /> : <HiChevronUp size={14} />}
                </button>
            ) : (
                <span className="download-selection-count">
                    {count} {count === 1 ? 'file' : 'files'} selected
                </span>
            )}
            {items && listOpen && (
                <ul id="download-selection-list" className="download-selection-list" aria-label="Selected files">
                    {items.map((it) => (
                        <li key={it.key}>
                            <span className="download-selection-list__name" title={it.name}>{it.name}</span>
                            <button type="button" className="download-selection-list__remove" aria-label={`Remove ${it.name}`}
                                onClick={() => onRemove?.(it.key)}>
                                <IoCloseOutline size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {onShare && (
                <button
                    type="button"
                    className="download-selection-download"
                    onClick={onShare}
                >
                    <IoShareSocialOutline size={16} /> Share selected
                </button>
            )}
            <button
                type="button"
                className="download-selection-download"
                onClick={onDownload}
            >
                <HiDownload size={16} /> {mode === 'download' ? 'Download selected' : 'Request download'}
            </button>
            <button
                type="button"
                className="download-selection-clear"
                onClick={onClear}
            >
                <IoCloseOutline size={18} /> Clear
            </button>
        </div>
    );
}
